import { createContext, useContext } from 'react';

import type { Tone } from './tone-icons';

export type ToastTone = Tone;
export type ToastDismissReason = 'timeout' | 'user' | 'action' | 'programmatic';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /** Default 'info'. */
  tone?: ToastTone;
  action?: ToastAction | undefined;
  /** Milliseconds on screen; null = until dismissed or actioned. Default 5000, errors 8000. */
  duration?: number | null;
  /** Same key replaces the existing toast instead of stacking (the SW update toast). */
  key?: string;
  onDismiss?: (reason: ToastDismissReason) => void;
}

/** One toast as the provider holds it — the resolved form of `ToastOptions`. */
export interface ToastRecord {
  id: string;
  message: string;
  tone: ToastTone;
  action: ToastOptions['action'];
  duration: number | null;
  key: string | undefined;
  onDismiss: ToastOptions['onDismiss'];
}

export interface ToastApi {
  /** Returns the toast's id, for a later `dismiss`. */
  show: (message: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
}

export const ToastContext = createContext<ToastApi | null>(null);

/**
 * Transient, non-blocking feedback: "Saved", "Attempt deleted", "A new version
 * is ready". Errors that need reading stay inline in a StatusBanner — a toast
 * leaves before a slow reader has finished it.
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}
